import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Check } from "lucide-react";
import { getOnboarding, saveOnboardingSection, submitAgreements, completeOnboarding, errText } from "@/lib/api";
import { Field, TextInput, TextArea, Select, PillGroup, YesNo, AckCheck, SubmitBar, FormShell } from "@/lib/forms";
import { Star, Heart } from "@/components/Doodles";
import { CREATORS } from "@/data/characters";

const STEPS = ["YOUR INFO", "AGREEMENTS", "YOUR PROFILE", "PROJECT READINESS", "COMPLETE"];
const LANES = CREATORS.map((c) => c.lane);
const humanize = (o) => o.replaceAll("_", " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

const StepDots = ({ step, sections }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="onboarding-steps">
    {STEPS.map((s, i) => {
      const doneKeys = ["info", "agreements", "profile", "readiness"];
      const isDone = i < 4 && sections?.[doneKeys[i]];
      return (
        <div key={s} className={`flex items-center gap-1.5 border px-2.5 py-1 font-head text-[10px] font-extrabold uppercase tracking-wide ${i === step ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : isDone ? "border-gold/50 bg-onyx text-goldlight" : "border-silver/30 bg-charcoal text-silver/70"}`}>
          {isDone && <Check size={12} />} {i + 1}. {s}
        </div>
      );
    })}
  </div>
);

const DocPanel = ({ doc, checked, onCheck, perms, setPerms }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-silver/25 bg-charcoal" data-testid={`doc-${doc.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 p-4 text-left" data-testid={`doc-toggle-${doc.key}`}>
        <div>
          <p className="font-head text-base font-extrabold uppercase tracking-tight">{doc.name} <span className="ml-1 font-mono text-[10px] text-silver/50">v{doc.version}</span></p>
          <p className="font-body text-xs text-silver/60">{doc.summary}</p>
        </div>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="border-t border-gold/15 p-4 pl-8 font-body text-sm leading-relaxed text-silver/80 list-disc space-y-1.5">
          {doc.clauses.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
      {doc.key === "media_release" && (
        <div className="space-y-4 border-t border-gold/15 p-4">
          {doc.special_permissions.map((sp) => (
            <Field key={sp.key} label={sp.label} required>
              <PillGroup value={perms[sp.key]} onChange={(x) => setPerms((p) => ({ ...p, [sp.key]: x }))} options={sp.options.map((o) => o)} testId={`perm-${sp.key}`} format={humanize} />
            </Field>
          ))}
        </div>
      )}
      <div className="border-t border-gold/15 p-4">
        <AckCheck checked={checked} onChange={onCheck} testId={`ack-doc-${doc.key}`}>
          I have read and acknowledge the <strong>{doc.name}</strong> (v{doc.version}).
        </AckCheck>
      </div>
    </div>
  );
};

export default function Onboarding() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [v, setV] = useState({});
  const [acks, setAcks] = useState({});
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  useEffect(() => {
    getOnboarding(token)
      .then((data) => {
        setState(data);
        setV(data.participant);
        const sc = data.participant.sections_complete || {};
        const ackMap = {};
        data.acknowledgments.forEach((a) => { ackMap[a.document_key] = true; });
        setAcks(ackMap);
        setPerms(data.participant.media_preferences || {});
        if (data.participant.onboarding_status === "complete") setStep(4);
        else if (!sc.info) setStep(0);
        else if (!sc.agreements) setStep(1);
        else if (!sc.profile) setStep(2);
        else if (!sc.readiness) setStep(3);
        else setStep(4);
      })
      .catch((e) => setError(errText(e)));
  }, [token]);

  if (error) {
    return (
      <div className="grain-dark mx-auto max-w-xl bg-onyx px-4 py-32 text-center text-cream">
        <h1 className="font-display text-4xl uppercase text-metallic">Hmm.</h1>
        <p className="mt-4 font-body text-silver/70" data-testid="onboarding-error">{error}</p>
      </div>
    );
  }
  if (!state) return <div className="bg-onyx py-40 text-center font-head font-bold uppercase text-silver/50">Opening your onboarding...</div>;

  const p = state.participant;
  const sections = v.sections_complete || p.sections_complete || {};
  const complete = v.onboarding_status === "complete" || p.onboarding_status === "complete";

  const saveSection = async (section, data, nextStep) => {
    setSaving(true);
    try {
      const updated = await saveOnboardingSection(token, section, data);
      setV(updated);
      toast.success("Saved ⭐");
      setStep(nextStep);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAgreements = async (e) => {
    e.preventDefault();
    const docs = state.documents;
    if (docs.some((d) => !acks[d.key])) return toast.error("All agreements must be acknowledged to continue.");
    const media = docs.find((d) => d.key === "media_release");
    if (media.special_permissions.some((sp) => !perms[sp.key])) return toast.error("This one's required — make your media release choices.");
    setSaving(true);
    try {
      await submitAgreements(token, docs.map((d) => ({
        document_key: d.key,
        special_permissions: d.key === "media_release" ? perms : null,
      })));
      setV((prev) => ({ ...prev, sections_complete: { ...(prev.sections_complete || {}), agreements: true } }));
      toast.success("Agreements acknowledged ⭐");
      setStep(2);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await completeOnboarding(token);
      setV((prev) => ({ ...prev, onboarding_status: "complete" }));
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="grain-dark relative bg-onyx py-14 text-cream sm:py-20" data-testid="onboarding-page">
      <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
      <div className="relative mx-auto max-w-[880px] px-4 sm:px-6">
        <p className="font-marker text-lg text-goldlight">welcome home ♡</p>
        <h1 className="mt-1 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">
          {complete ? "You're officially in the Village. ⭐" : `Onboarding: ${p.display_name || p.legal_name}`}
        </h1>
        {!complete && <p className="mt-3 max-w-xl font-body text-silver/70">Five quick steps to lock in your spot. Your progress saves as you go.</p>}
        <div className="mt-6"><StepDots step={complete ? 4 : step} sections={sections} /></div>

        <div className="mt-8">
          {complete ? (
            <FormShell marker="we made this ♡">
              <div className="py-8 text-center" data-testid="onboarding-complete">
                <Star className="mx-auto h-16 w-16" />
                <h2 className="mt-6 font-display text-4xl uppercase tracking-tight">You're officially in the Village. ⭐</h2>
                <p className="mx-auto mt-4 max-w-md font-body text-silver/75">
                  Your info, agreements, profile, and project readiness are all locked in. When a Campaign Lab or Issue
                  matches your lane and availability, we'll reach out. Onboarding doesn't guarantee a project assignment
                  or paid work — but you're on the roster now.
                </p>
                <p className="mt-6 font-marker text-xl text-goldlight">see you on set ✦</p>
                <Link to={`/missions/${token}`} data-testid="ob-mission-board-btn"
                  className="btn-sweep mt-6 inline-block border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-4 font-head text-base font-extrabold uppercase tracking-[0.15em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform hover:-translate-y-0.5">
                  Open Your Mission Board
                </Link>
              </div>
            </FormShell>
          ) : step === 0 ? (
            <FormShell marker="step 1 of 5">
              <form onSubmit={(e) => { e.preventDefault(); saveSection("info", v, 1); }} className="space-y-5" data-testid="onboarding-info-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Legal name" required><TextInput required value={v.legal_name} onChange={(x) => set("legal_name", x)} testId="ob-legal-name" /></Field>
                  <Field label="Display / professional name" required><TextInput required value={v.display_name} onChange={(x) => set("display_name", x)} testId="ob-display-name" /></Field>
                  <Field label="Phone" required><TextInput required value={v.phone} onChange={(x) => set("phone", x)} testId="ob-phone" /></Field>
                  <Field label="Pronouns (optional)"><TextInput value={v.pronouns} onChange={(x) => set("pronouns", x)} testId="ob-pronouns" /></Field>
                  <Field label="Emergency contact name" required><TextInput required value={v.emergency_contact_name} onChange={(x) => set("emergency_contact_name", x)} testId="ob-emergency-name" /></Field>
                  <Field label="Emergency contact phone" required><TextInput required value={v.emergency_contact_phone} onChange={(x) => set("emergency_contact_phone", x)} testId="ob-emergency-phone" /></Field>
                  <Field label="School"><TextInput value={v.school_name} onChange={(x) => set("school_name", x)} testId="ob-school" /></Field>
                  <Field label="Program"><TextInput value={v.school_program} onChange={(x) => set("school_program", x)} testId="ob-program" /></Field>
                  <Field label="Graduation date"><TextInput value={v.graduation_date} onChange={(x) => set("graduation_date", x)} testId="ob-graduation" /></Field>
                  <Field label="Transportation"><TextInput value={v.transportation_access} onChange={(x) => set("transportation_access", x)} testId="ob-transportation" /></Field>
                </div>
                <Field label="Primary lane"><PillGroup value={v.primary_lane} onChange={(x) => set("primary_lane", x)} options={LANES} testId="ob-primary-lane" /></Field>
                <Field label="Secondary lanes"><PillGroup multi value={v.secondary_lanes} onChange={(x) => set("secondary_lanes", x)} options={LANES} testId="ob-secondary-lanes" /></Field>
                <Field label="Availability" required><TextInput required value={v.availability} onChange={(x) => set("availability", x)} testId="ob-availability" placeholder="Weeknights after 5, Saturdays..." /></Field>
                <SubmitBar loading={saving} label="Save & Continue" loadingLabel="SAVING..." testId="ob-save-info" />
              </form>
            </FormShell>
          ) : step === 1 ? (
            <FormShell marker="step 2 of 5">
              <form onSubmit={saveAgreements} className="space-y-4" data-testid="onboarding-agreements-form">
                <p className="font-body text-sm text-silver/70">
                  These are the Village's working agreements — readable, versioned, and in plain language. Open each one,
                  read it, and acknowledge. The Media Release also asks for your personal choices.
                </p>
                {state.documents.map((d) => (
                  <DocPanel key={d.key} doc={d} checked={acks[d.key]} onCheck={(c) => setAcks((prev) => ({ ...prev, [d.key]: c }))} perms={perms} setPerms={setPerms} />
                ))}
                <SubmitBar loading={saving} label="Acknowledge & Continue" loadingLabel="SAVING..." testId="ob-save-agreements" />
              </form>
            </FormShell>
          ) : step === 2 ? (
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
              <FormShell marker="step 3 of 5">
                <form onSubmit={(e) => { e.preventDefault(); saveSection("profile", v, 3); }} className="space-y-5" data-testid="onboarding-profile-form">
                  <Field label="Public display name" required><TextInput required value={v.display_name} onChange={(x) => set("display_name", x)} testId="ob-profile-name" /></Field>
                  <Field label="Short bio" required><TextArea required rows={3} value={v.short_bio} onChange={(x) => set("short_bio", x)} testId="ob-bio" placeholder="Who you are, what you make..." /></Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="City / State"><TextInput value={v.city_state} onChange={(x) => set("city_state", x)} testId="ob-city" /></Field>
                    <Field label="Headshot URL"><TextInput value={v.headshot_url} onChange={(x) => set("headshot_url", x)} testId="ob-headshot" placeholder="Link to a photo of you" /></Field>
                    <Field label="Skills"><TextInput value={v.skills} onChange={(x) => set("skills", x)} testId="ob-skills" /></Field>
                    <Field label="Tools"><TextInput value={v.software_tools} onChange={(x) => set("software_tools", x)} testId="ob-tools" /></Field>
                    <Field label="Portfolio URL"><TextInput value={v.portfolio_url} onChange={(x) => set("portfolio_url", x)} testId="ob-portfolio" /></Field>
                    <Field label="Social links"><TextInput value={v.social_links} onChange={(x) => set("social_links", x)} testId="ob-socials" /></Field>
                  </div>
                  <Field label="Goals"><TextArea rows={2} value={v.goals} onChange={(x) => set("goals", x)} testId="ob-goals" /></Field>
                  <div className="border border-dashed border-gold/40 bg-onyx p-4 text-center">
                    <p className="font-marker text-lg text-goldlight">BLV AVATAR — COMING SOON ✦</p>
                    <p className="font-body text-xs text-silver/60">Soon you'll be able to rep an illustrated Villager avatar instead of a headshot.</p>
                  </div>
                  <SubmitBar loading={saving} label="Save & Continue" loadingLabel="SAVING..." testId="ob-save-profile" />
                </form>
              </FormShell>
              <div>
                <p className="font-head text-sm font-extrabold uppercase tracking-widest text-cream">This is how you'll appear in the Village. ⭐</p>
                <div className="mt-3 gold-frame bg-charcoal" data-testid="profile-preview">
                  <div className="flex h-44 items-center justify-center border-b border-gold/30 bg-onyx">
                    {v.headshot_url ? <img src={v.headshot_url} alt="" className="h-full w-full object-cover" /> : <Heart className="h-14 w-14" />}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-2xl uppercase tracking-tight">{v.display_name || "Your Name"}</h3>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-goldlight">{v.primary_lane || "Your lane"}</p>
                    <p className="mt-2 font-body text-sm text-silver/70">{v.short_bio || "Your bio will live here."}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : step === 3 ? (
            <FormShell marker="step 4 of 5">
              <form onSubmit={(e) => { e.preventDefault(); saveSection("readiness", v, 4); }} className="space-y-5" data-testid="onboarding-readiness-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Equipment / tools you'll bring"><TextInput value={v.equipment_access} onChange={(x) => set("equipment_access", x)} testId="ob-equipment" /></Field>
                  <Field label="Access needs"><TextInput value={v.equipment_needs} onChange={(x) => set("equipment_needs", x)} testId="ob-needs" /></Field>
                  <Field label="Available for in-person production?"><YesNo value={v.in_person_ok} onChange={(x) => set("in_person_ok", x)} testId="ob-in-person" /></Field>
                  <Field label="Available for virtual work?"><YesNo value={v.virtual_ok} onChange={(x) => set("virtual_ok", x)} testId="ob-virtual" /></Field>
                  <Field label="Comfortable working from briefs?"><YesNo value={v.briefs_ok} onChange={(x) => set("briefs_ok", x)} testId="ob-briefs" /></Field>
                  <Field label="Comfortable brainstorming with a crew?"><YesNo value={v.brainstorming_ok} onChange={(x) => set("brainstorming_ok", x)} testId="ob-brainstorm" /></Field>
                  <Field label="Seeking school credit?"><YesNo value={v.school_credit_requested === true ? "Yes" : v.school_credit_requested === false ? "No" : v.school_credit_requested} onChange={(x) => set("school_credit_requested", x)} testId="ob-credit" /></Field>
                </div>
                <Field label="School requirements (if seeking credit)"><TextInput value={v.school_requirements} onChange={(x) => set("school_requirements", x)} testId="ob-school-reqs" /></Field>
                <Field label="Accessibility / scheduling considerations"><TextArea rows={2} value={v.accessibility_considerations} onChange={(x) => set("accessibility_considerations", x)} testId="ob-accessibility" /></Field>
                <Field label="Creative boundaries / production considerations"><TextArea rows={2} value={v.production_considerations} onChange={(x) => set("production_considerations", x)} testId="ob-boundaries" /></Field>
                <SubmitBar loading={saving} label="Save & Continue" loadingLabel="SAVING..." testId="ob-save-readiness" />
              </form>
            </FormShell>
          ) : (
            <FormShell marker="step 5 of 5">
              <div className="py-6 text-center" data-testid="onboarding-final-step">
                <Star className="mx-auto h-12 w-12" />
                <h2 className="mt-5 font-display text-3xl uppercase tracking-tight">One last tap.</h2>
                <p className="mx-auto mt-3 max-w-md font-body text-sm text-silver/70">
                  Everything's saved. Completing onboarding confirms your info, agreements, profile, and readiness —
                  and makes you an official Villager. It does not guarantee a project assignment or paid work.
                </p>
                <button onClick={finish} disabled={saving} data-testid="ob-complete-btn"
                  className="btn-sweep mt-6 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-4 font-head text-lg font-extrabold uppercase tracking-wide text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  {saving ? "LOCKING IT IN..." : "Complete Onboarding ⭐"}
                </button>
              </div>
            </FormShell>
          )}
        </div>
      </div>
    </section>
  );
}
